// Store references to our event handlers so we can remove them.
/** @type {{ keydown?: (event: KeyboardEvent) => void }} */
const trapFocusHandlers = {};

/**
 * Get all focusable elements within a container.
 * @param {HTMLElement | null} container - The container to get focusable elements from.
 * @returns {HTMLElement[]} An array of focusable elements.
 */
export function getFocusableElements(container) {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

/**
 * Traps focus inside container; optionally moves focus to elementToFocus (Tab cycles first↔last).
 * @param {Element} container - Dialog/container element
 * @param {Element} [elementToFocus] - Element to focus when trap is applied (default: container)
 * @param {{ focusImmediately?: boolean }} [options] - If focusImmediately is false, do not focus on open (user must Tab to enter)
 */
export function trapFocus(container, elementToFocus = container, options = {}) {
  const { focusImmediately = true } = options;

  removeTrapFocus();

  trapFocusHandlers.keydown = (event) => {
    if (event.code.toUpperCase() !== 'TAB') return;
    // Recompute every Tab so the trap stays correct after the dialog re-renders.
    const elements = getFocusableElements(/** @type {HTMLElement} */ (container));
    if (!elements.length) return;
    const first = elements[0];
    const last = elements[elements.length - 1];
    const active = document.activeElement;
    if (!container.contains(active)) {
      event.preventDefault();
      if (event.shiftKey) last?.focus(); else first?.focus();
      return;
    }
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first?.focus();
    } else if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last?.focus();
    }
  };

  document.addEventListener('keydown', trapFocusHandlers.keydown);

  if (!focusImmediately) return;

  const target = elementToFocus || container;
  if (target && typeof target.focus === 'function') target.focus();
  if (target?.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(target.type) && target.value) {
    target.setSelectionRange(0, target.value.length);
  }
}

/**
 * Removes focus trap and optionally restores focus to element.
 * @param {Element | null} [elementToFocus]
 */
export function removeTrapFocus(elementToFocus = null) {
  if (trapFocusHandlers.keydown) document.removeEventListener('keydown', trapFocusHandlers.keydown);
  if (elementToFocus && typeof elementToFocus.focus === 'function') elementToFocus.focus({ preventScroll: true });
}
